export interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  categorySlug: string;
  author: string;
  publishDate: string;
  readTime: number;
  imageSeq: string;
  imageQuery: string;
  tags: string[];
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
}

export const categories: Category[] = [
  {
    id: 'chuchu',
    name: '典故出处',
    slug: 'chuchu',
    description: '追溯黄游下戟的文献源头与历史记载',
    icon: 'ri-book-open-line',
  },
  {
    id: 'beijing',
    name: '历史背景',
    slug: 'beijing',
    description: '探寻典故产生的历史时代与社会环境',
    icon: 'ri-ancient-gate-line',
  },
  {
    id: 'yuyi',
    name: '文化寓意',
    slug: 'yuyi',
    description: '解读典故背后的深层文化内涵与哲理',
    icon: 'ri-lightbulb-line',
  },
  {
    id: 'chuancheng',
    name: '诗词传承',
    slug: 'chuancheng',
    description: '梳理典故在历代诗词文学中的传承与演变',
    icon: 'ri-quill-pen-line',
  },
];

export const articles: Article[] = [
  {
    id: 'huangyou-xiashuo-chuchu',
    title: '黄游下戟的文献出处考辨',
    excerpt: '黄游下戟这一典故究竟最早见于何典？本文通过对先秦至两汉文献的系统梳理，追溯其最早的文献记载与演变脉络。',
    content: `<p>黄游下戟，作为中国古代文化中一个极具分量的经典意象，其文献出处一直是学界关注的焦点。本文通过对先秦至两汉时期相关文献的系统梳理，尝试还原这一典故的原始面貌。</p>

<h2>一、先秦文献中的雏形</h2>
<p>追溯黄游下戟的源头，我们需要回到战国时期。在《庄子·逍遥游》中，虽然没有直接出现"黄游下戟"四字，但其中关于"游"的哲学阐释，已经为后世这一典故的形成奠定了思想基础。庄子云："乘天地之正，而御六气之辩，以游无穷者。"这种超然物外的游心境界，正是"黄游"的精神内核。</p>

<p>同时期的《楚辞》中，屈原在《离骚》《九歌》等篇章中大量使用"游"字，如"忽反顾以游目兮""聊逍遥以相羊"，其中的逍遥之游，同样构成了这一典故的文化底色。</p>

<h2>二、汉代文献的确证</h2>
<p>到了汉代，"黄游下戟"作为一个完整的成语表达开始出现在各类文献中。西汉刘向编纂的《说苑》中，有一则关于古之贤者持戟而游的记载，被视为该典故的早期雏形。东汉班固《汉书·艺文志》中，则在著录诸子著作时提及相关典故。</p>

<p>值得注意的是，东汉许慎在《说文解字》中对"戟"字的释义为："有枝兵也。"这种对兵器本身的解读，为后世理解"下戟"的文化意涵提供了重要的文字学依据。</p>

<h2>三、魏晋时期的定型</h2>
<p>魏晋南北朝时期，随着玄学的兴起和文人个体意识的觉醒，"黄游下戟"这一典故获得了更加丰富的文化内涵。在《世说新语》中，这一典故被多次引用，用以形容士人的超脱与风骨。至此，"黄游下戟"作为一个成熟的文学意象和文化符号，正式进入中国文学的经典谱系。</p>

<blockquote>
  <p>盖闻黄游者，逍遥乎天地之间；下戟者，示人以无为之道。此古之贤者处世之方也。</p>
</blockquote>`,
    category: '典故出处',
    categorySlug: 'chuchu',
    author: '文渊阁',
    publishDate: '2025-12-15',
    readTime: 12,
    imageSeq: 'art-chuchu-01',
    imageQuery: 'Ancient%20Chinese%20scrolls%20and%20bamboo%20slips%20with%20calligraphy%2C%20warm%20lighting%2C%20scholarly%20atmosphere%2C%20traditional%20Chinese%20study%20room%2C%20ink%20and%20brush%20on%20wooden%20desk%2C%20soft%20natural%20light%2C%20elegant%20and%20refined%20composition',
    tags: ['文献考据', '出处追溯', '先秦典籍'],
  },
  {
    id: 'huangyou-xiashuo-gushi',
    title: '黄游下戟：一段被遗忘的贤者故事',
    excerpt: '在春秋战国的动荡年代，一位名叫黄游的贤士以"下戟"之举演绎了何为真正的智慧与勇气。这段尘封的故事值得我们重新发现。',
    content: `<p>在中国古代浩瀚的历史长河中，有许多动人的故事因为年代久远而逐渐被人遗忘。"黄游下戟"正是这样一个承载着古人智慧与风骨的经典故事。今天，让我们回到那个群雄并起的时代，重新聆听这位贤者的传奇。</p>

<h2>一、春秋乱世中的贤者</h2>
<p>相传在春秋时期，有一位名叫黄游的贤士，他精通兵法韬略，却不愿为任何诸侯效力。当时，列国纷争不断，各国君主争相延揽人才，黄游自然也成为各方争夺的对象。</p>

<p>一日，某大国君主派遣使者携带重金前来邀请黄游出山。面对丰厚的条件，黄游并未动心。他站在自家庭院之中，手持一柄长戟——这是那个时代士人身份的象征。</p>

<h2>二、"下戟"之举的深意</h2>
<p>当使者再三恳请时，黄游缓缓将手中的长戟放下——这便是"下戟"的由来。这一看似简单的动作，却蕴含着极为深刻的含义。</p>

<p>在古代，"戟"不仅是武器，更是权力、武力与征伐的象征。"下戟"意味着放下武力，拒绝以暴力谋取功名。黄游以此表明自己不愿置身于诸侯争霸的漩涡之中，选择了清静自守的人生道路。</p>

<h2>三、故事的深远影响</h2>
<p>黄游的这一举动在当时引起了巨大反响。许多有识之士纷纷效仿，形成了以"下戟"来表达退隐之志的文化传统。这一典故后来被收入各类文献典籍，成为中国文化中"不慕荣利""超然物外"精神的经典象征。</p>

<blockquote>
  <p>戟者，兵之象也；下之，则示不争。黄游以一戟之轻重，明天下之大义，可谓智者矣。</p>
</blockquote>

<p>这个故事告诉我们，真正的勇气并不总是在战场之上，更多的时候，它体现在面对诱惑时的坚守，以及对内心信念的忠诚。黄游下戟，虽距今已有两千余年，但其精神内核依然闪耀着智慧的光芒。</p>`,
    category: '历史背景',
    categorySlug: 'beijing',
    author: '史海钩沉',
    publishDate: '2025-11-20',
    readTime: 10,
    imageSeq: 'art-gushi-01',
    imageQuery: 'Ancient%20Chinese%20warrior%20scholar%20in%20traditional%20robes%20holding%20a%20halberd%2C%20ancient%20Chinese%20courtyard%2C%20autumn%20leaves%2C%20misty%20atmosphere%2C%20ink%20wash%20painting%20style%2C%20elegant%20and%20serene%20mood',
    tags: ['历史故事', '春秋时期', '贤者风范'],
  },
  {
    id: 'huangyou-xiashuo-xiangzheng',
    title: '下戟之喻：解读黄游下戟的象征意义',
    excerpt: '戟在中国古代文化中具有丰富的象征义涵。本文从符号学和文化学的角度，深入剖析黄游下戟这一意象的多重象征意义。',
    content: `<p>"黄游下戟"之所以能够成为流传千古的文化典故，关键在于其中蕴含的丰富象征意义。本文将从多个维度，对这一典故的象征体系进行深度解读。</p>

<h2>一、戟的双重象征</h2>
<p>在中国古代文化中，"戟"这一意象具有独特而丰富的象征内涵。一方面，戟作为冷兵器时代的重要武器，象征着武力、权威与征伐；另一方面，戟也是士人身份的标识——"持戟"代表着一种身份认同和社会地位。</p>

<p>因此，"戟"本身就处于武力与文明、征伐与礼制的交汇点上，这使得"下戟"这一动作获得了超越表层含义的深刻文化意蕴。</p>

<h2>二、"下"的动作意涵</h2>
<p>"下"在中国文化中同样具有丰富的意义层次。它既是一个空间概念——从高到低、从悬到置；也是一个价值判断——从显到隐、从争到让。</p>

<p>当黄游"下戟"之时，这一动作至少包含了三重意味：其一，放下武器，拒绝暴力；其二，放下身段，不慕荣利；其三，放下执念，回归本真。这种多层次的意义叠加，使得"黄游下戟"成为一个极具张力的文化符号。</p>

<h2>三、黄游下戟的精神内核</h2>
<p>综合来看，黄游下戟的核心精神可以概括为"以退为进，以柔克刚"。在群雄逐鹿的时代背景下，黄游选择了一条不同寻常的道路——不以武力争天下，而以德行立世间。</p>

<p>这种精神与道家"无为而无不为"的思想一脉相承，也与儒家"穷则独善其身"的理念遥相呼应。黄游下戟因此成为中国传统文化中"士人精神"的重要象征。</p>

<blockquote>
  <p>夫下戟者，非怯也，乃勇之至也。知所止，明所守，虽千万人吾往矣，虽万钟之禄吾辞矣。此黄游之所以为黄游也。</p>
</blockquote>`,
    category: '文化寓意',
    categorySlug: 'yuyi',
    author: '意象解读',
    publishDate: '2025-10-08',
    readTime: 8,
    imageSeq: 'art-xiangzheng-01',
    imageQuery: 'Symbolic%20ancient%20Chinese%20halberd%20weapon%20resting%20against%20a%20stone%20wall%2C%20soft%20diffused%20lighting%2C%20minimalist%20composition%2C%20ink%20wash%20aesthetic%2C%20serene%20and%20contemplative%20atmosphere',
    tags: ['象征意义', '文化符号', '士人精神'],
  },
  {
    id: 'huangyou-xiashuo-zhexue',
    title: '黄游下戟与道家无为思想的渊源',
    excerpt: '黄游下戟的哲学根基深植于道家思想。本文探讨这一典故与老子"无为"、庄子"逍遥"之间的内在关联。',
    content: `<p>黄游下戟作为中国传统文化中的经典典故，其精神底色与道家思想有着密不可分的关系。本文尝试从哲学层面，揭示这一典故与道家核心概念之间的深层关联。</p>

<h2>一、老子"无为"与"下戟"</h2>
<p>老子在《道德经》中反复强调"无为"的重要性："为无为，则无不治。"这种"无为"并非消极的不作为，而是不妄为、不强为的智慧。黄游的"下戟"之举，正是这种"无为"思想的具体实践。</p>

<p>值得注意的是，《道德经》中还有"夫唯不争，故天下莫能与之争"的论述。黄游放下象征武力的戟，表面上是放弃竞争，实际上却以更高的道德境界赢得了世人的尊重——这正是"不争而胜"的绝佳例证。</p>

<h2>二、庄子"逍遥游"的精神境界</h2>
<p>庄子的"逍遥游"思想，强调的是超越世俗羁绊、达到精神绝对自由的境界。黄游的行为中，"黄"与"游"的关联，很可能与庄子思想有着某种呼应。</p>

<p>在庄子看来，真正的自由来自于对世俗价值的超越。黄游选择"下戟"而非持戟争雄，正是对这种超越精神的身体力行。他不为名利所困，不为权势所累，以其独特的方式实现了庄周所说的"游乎四海之外"。</p>

<h2>三、儒道融合中的黄游形象</h2>
<p>值得关注的是，在后世的传播过程中，黄游下戟的形象逐渐超越了单一的道家框架，融入了儒家的价值理念。儒家强调"用之则行，舍之则藏"的处世哲学，而黄游在拒绝出仕的同时并未否定社会责任，这种"有所为有所不为"的态度，恰恰体现了儒道融合的文化特征。</p>

<blockquote>
  <p>道之不行，已知之矣。然不以其道，虽三公之位不易也。此黄游下戟之义，与圣人"不义而富且贵，于我如浮云"之旨，何其相似乃尔！</p>
</blockquote>`,
    category: '文化寓意',
    categorySlug: 'yuyi',
    author: '哲思探微',
    publishDate: '2025-09-15',
    readTime: 11,
    imageSeq: 'art-zhexue-01',
    imageQuery: 'Ancient%20Chinese%20philosophy%20scene%2C%20abstract%20ink%20wash%20landscape%20with%20misty%20mountains%2C%20contemplative%20atmosphere%2C%20Taoist%20philosophy%20concept%2C%20minimalist%20composition%2C%20serene%20and%20profound%20mood',
    tags: ['道家思想', '无为', '逍遥游', '儒道融合'],
  },
  {
    id: 'huangyou-xiashuo-tangshi',
    title: '唐诗中的黄游下戟意象',
    excerpt: '唐代诗人在创作中如何化用"黄游下戟"这一典故？本文梳理了李白、杜甫、王维等诗坛巨匠的诗作，展现典故在诗歌中的艺术呈现。',
    content: `<p>唐诗作为中国古典诗歌的巅峰，对历史典故的化用达到了出神入化的境界。"黄游下戟"这一典故，在唐代诗人的笔下获得了新的艺术生命。本文将带您领略唐诗中的这一经典意象。</p>

<h2>一、李白的洒脱化用</h2>
<p>李白作为唐代最富浪漫主义精神的诗人，对"黄游下戟"的理解最为贴近其逍遥超脱的精神内核。在其名篇《将进酒》中，"钟鼓馔玉不足贵，但愿长醉不复醒"的豪迈气概，与黄游下戟的超然精神遥相呼应。</p>

<p>在《行路难》中，李白更是直接化用了这一典故的精神内涵："长风破浪会有时，直挂云帆济沧海。"这种看似进取的姿态背后，实则隐藏着对功名利禄的深切放下——只有放下对一时得失的执念，才能迎来真正的远航。</p>

<h2>二、杜甫的深沉反思</h2>
<p>与李白不同，杜甫对这一典故的处理更加深沉和内敛。在《登高》中，"无边落木萧萧下，不尽长江滚滚来"虽未直接提及黄游下戟，但那种深沉的历史感和对世事无常的体悟，与典故中的"放下"精神有着内在的共鸣。</p>

<p>杜甫在《茅屋为秋风所破歌》中表现出的"安得广厦千万间，大庇天下寒士俱欢颜"的胸怀，也正是黄游下戟精神的另一种展现——放下个人的得失，心系天下苍生。</p>

<h2>三、王维的诗画交融</h2>
<p>王维的诗歌以其诗中有画、画中有诗的独特风格著称。在其山水田园诗中，"黄游下戟"的意象被转化为一种静谧、超脱的审美体验。</p>

<p>《山居秋暝》中"空山新雨后，天气晚来秋。明月松间照，清泉石上流"的意境，正是黄游下戟所象征的那种放下尘世纷扰之后的精神境界——在自然中找到了内心的宁静与自由。</p>

<blockquote>
  <p>唐人用典，不拘泥于文字，而重在精神。黄游下戟之意，在唐诗中或显或隐，然其超然物外之旨，贯穿于有唐一代之诗歌创作，可谓深入人心矣。</p>
</blockquote>`,
    category: '诗词传承',
    categorySlug: 'chuancheng',
    author: '诗海寻踪',
    publishDate: '2025-08-22',
    readTime: 9,
    imageSeq: 'art-tangshi-01',
    imageQuery: 'Tang%20dynasty%20poetry%20scene%2C%20ancient%20Chinese%20scholar%20writing%20calligraphy%20under%20moonlight%2C%20traditional%20Chinese%20garden%2C%20ink%20wash%20painting%20style%2C%20elegant%20and%20romantic%20atmosphere',
    tags: ['唐诗', '李白', '杜甫', '王维', '诗歌意象'],
  },
  {
    id: 'huangyou-xiashuo-songci',
    title: '宋词中的下戟遗韵',
    excerpt: '宋代词人以更加细腻的笔触将黄游下戟的意境融入词作。从苏轼到辛弃疾，看宋代文人是如何传承并发展这一典故的。',
    content: `<p>宋代是中国词学发展的黄金时代。在这一时期，黄游下戟的典故以更加丰富多样的形式进入了词人的创作视野，产生了一大批脍炙人口的名篇佳作。</p>

<h2>一、苏轼的旷达与超越</h2>
<p>苏轼对黄游下戟的精神有着极为深刻的理解。在《定风波》中，"莫听穿林打叶声，何妨吟啸且徐行。竹杖芒鞋轻胜马，谁怕？一蓑烟雨任平生"——这种面对人生风雨时的从容与淡定，正是黄游下戟精神的绝佳体现。</p>

<p>在《前赤壁赋》中，苏轼更是将"下戟"的精神发挥到了哲学的高度："寄蜉蝣于天地，渺沧海之一粟。哀吾生之须臾，羡长江之无穷。"这种对个体生命有限性的深刻认识，反而成就了一种超越性的精神自由。</p>

<h2>二、辛弃疾的豪放与无奈</h2>
<p>辛弃疾的词中，黄游下戟的意象更多了一份壮志难酬的悲凉。在《破阵子》中，"了却君王天下事，赢得生前身后名。可怜白发生！"表面上是对功名的追求，实则暗含了对"下戟"——放下执念——的深切渴望。</p>

<p>《永遇乐·京口北固亭怀古》中"千古江山，英雄无觅孙仲谋处"的慨叹，同样折射出一种面对历史变迁时的无力感，而这种无力感恰恰是理解黄游下戟精神的重要维度。</p>

<h2>三、李清照的婉约诠释</h2>
<p>李清照以女性词人特有的敏感与细腻，为黄游下戟的典故注入了新的情感内涵。在《声声慢》中，"寻寻觅觅，冷冷清清，凄凄惨惨戚戚"的愁绪背后，是一种更加个人化的"放下"——放下故国之思、放下逝去的美好。</p>

<p>这种将宏大典故转化为个人情感体验的能力，正是李清照的伟大之处，也证明了黄游下戟这一典故具有无限的可阐释空间。</p>

<blockquote>
  <p>宋人用典，得其神而化其形。黄游下戟之意，入于词中，或为旷达，或为悲凉，或为婉约，变化万端而精神不渝，此宋词之妙也。</p>
</blockquote>`,
    category: '诗词传承',
    categorySlug: 'chuancheng',
    author: '词话撷英',
    publishDate: '2025-07-18',
    readTime: 10,
    imageSeq: 'art-songci-01',
    imageQuery: 'Song%20dynasty%20literati%20gathering%2C%20elegant%20Chinese%20garden%20with%20pavilion%2C%20scholars%20reciting%20poetry%2C%20soft%20candlelight%2C%20traditional%20Chinese%20aesthetic%2C%20refined%20and%20cultured%20atmosphere',
    tags: ['宋词', '苏轼', '辛弃疾', '李清照', '词学'],
  },
  {
    id: 'huangyou-xiashuo-xiandai',
    title: '黄游下戟精神的当代价值',
    excerpt: '在快节奏的现代社会中，黄游下戟所代表的"放下"智慧具有怎样的现实意义？本文探讨这一古老典故的现代启示。',
    content: `<p>在当今这个信息爆炸、竞争激烈的时代，黄游下戟这一古老典故反而展现出前所未有的现实意义。当"内卷"成为流行词、"焦虑"成为普遍情绪之时，古人的智慧或许能为我们提供一剂良方。</p>

<h2>一、"内卷"时代的"下戟"智慧</h2>
<p>当代社会的一个重要特征是普遍的"内卷"——人们为了在竞争中不落后而不断自我加压，结果却往往是身心俱疲而收效甚微。在这样的背景下，黄游下戟所传达的"适时放下"的智慧显得尤为珍贵。</p>

<p>"下戟"不是逃避，而是一种主动的选择——清醒地认识到什么是真正重要的，什么是可以放下的。这种选择的勇气，恰恰是当代人最为缺乏的。</p>

<h2>二、极简主义与黄游下戟</h2>
<p>近年来，极简主义（Minimalism）在全球范围内兴起，其核心理念——"少即是多"——与黄游下戟的精神有着异曲同工之妙。无论是物质层面的断舍离，还是精神层面的减法生活，本质上都是对"戟"的放下——放下不必要的负担，回归生活的本质。</p>

<h2>三、在喧嚣中寻找内心的宁静</h2>
<p>黄游下戟给予当代人的最重要启示，或许就是：在喧嚣中依然可以保持内心的宁静。信息时代的海量刺激让我们的注意力永远处于被分散的状态，而黄游的选择告诉我们——有时候，放下手中的"戟"（手机、焦虑、攀比），才能真正拥有自己的生活。</p>

<blockquote>
  <p>古今一也。黄游下戟之义，非独古人之事，亦今人之所需也。当世之人，若能于繁华中识得放下之道，则虽居闹市，亦可得山林之趣矣。</p>
</blockquote>

<p>黄游下戟的故事虽然发生在两千多年前，但其精神内核——对内心真实的坚守、对外在诱惑的超越——永远不会过时。在这个意义上，每一个时代的人们都需要属于自己的"下戟"时刻。</p>`,
    category: '文化寓意',
    categorySlug: 'yuyi',
    author: '古今对话',
    publishDate: '2025-06-30',
    readTime: 8,
    imageSeq: 'art-xiandai-01',
    imageQuery: 'Modern%20minimalist%20zen%20study%20room%2C%20blend%20of%20traditional%20Chinese%20and%20contemporary%20design%2C%20warm%20natural%20lighting%2C%20peaceful%20and%20contemplative%20atmosphere%2C%20clean%20lines%2C%20serene%20composition',
    tags: ['当代价值', '内卷', '极简主义', '心灵修养'],
  },
  {
    id: 'huangyou-xiashuo-yishu',
    title: '历代书画中的黄游下戟图景',
    excerpt: '从顾恺之到八大山人，中国历代画家如何以笔墨呈现黄游下戟的意境？本文带您欣赏这一典故在视觉艺术中的精彩演绎。',
    content: `<p>除了文学与哲学领域，黄游下戟的典故在中国传统视觉艺术中也留下了深刻的印记。历代画家以各自独特的笔墨语言，对这一典故进行了丰富多彩的视觉诠释。</p>

<h2>一、魏晋人物画中的黄游形象</h2>
<p>魏晋时期是中国人物画发展的重要阶段。据传顾恺之曾创作过以"黄游"为主题的人物画，虽然原作已经失传，但唐人摹本中仍可见其基本面貌：画中的黄游衣袂飘飘，手持长戟而立，神态超然。</p>

<p>这种以人物为中心、以神态传精神的绘画方式，奠定了后世"黄游图"的基本范式。画中人物的"神"重于"形"，正符合魏晋时期"传神写照"的绘画美学。</p>

<h2>二、宋元山水中的隐逸之趣</h2>
<p>宋元时期，随着文人画的兴起，"黄游下戟"的意象更多通过山水画的方式加以表现。画家们不再直接描绘黄游本人，而是通过山水意境来传达"下戟"之后的精神境界。</p>

<p>倪瓒的"一河两岸"构图，以其极简的笔墨和空灵的意境，完美体现了黄游下戟的精神——去繁就简，返璞归真。这种"逸笔草草，不求形似"的绘画风格，与黄游下戟的文化内涵形成了深层共鸣。</p>

<h2>三、明清写意中的个性表达</h2>
<p>到了明清时期，徐渭、八大山人等写意画家将黄游下戟的精神推向了更加个性化的表达。八大山人的花鸟画中那些白眼向人的鱼鸟，虽然看似与黄游下戟无关，实则正是"下戟"精神的极端表达——以最激烈的姿态表达最彻底的放下。</p>

<blockquote>
  <p>画者，心之迹也。黄游下戟之意，入于画中，不在形似，而在神会。观者若能于此中悟得放下之道，则画之妙得矣。</p>
</blockquote>`,
    category: '诗词传承',
    categorySlug: 'chuancheng',
    author: '丹青鉴赏',
    publishDate: '2025-05-12',
    readTime: 9,
    imageSeq: 'art-yishu-01',
    imageQuery: 'Traditional%20Chinese%20ink%20wash%20painting%20of%20a%20scholar%20in%20landscape%2C%20misty%20mountains%2C%20flowing%20water%2C%20ancient%20pine%20trees%2C%20elegant%20brushwork%2C%20classical%20Chinese%20art%20aesthetic',
    tags: ['书画艺术', '文人画', '视觉演绎', '顾恺之'],
  },
];